import {ReactNode} from 'react';
import {EyebrowPill} from './eyebrow-pill';
import {getSectionBgScheme} from '../appearance-data';

/**
 * Shared section chrome: surfaces, headings and the education backdrop.
 *
 * Two problems this solves:
 *  1. Every block used to paint the same flat white, so the page read as one
 *     endless column. `surface()` alternates a few tasteful grounds instead.
 *  2. Section titles were a bare `<h2>`. `SectionHeading` gives them a
 *     consistent, deliberate treatment.
 */

export type SurfaceKind = 'plain' | 'tint' | 'soft' | 'edu';

const SURFACE_KINDS: SurfaceKind[] = ['plain', 'tint', 'soft', 'edu'];

/**
 * Resolves what a section's background should be, given three inputs in order
 * of precedence:
 *   1. `override` — the block's own `bg_style` when the admin picked a specific
 *      surface on that one block (plain/tint/soft/edu). Always wins.
 *   2. the global default — Theme Options → "Section Backgrounds"
 *      (`section_bg_scheme`). This is the site-wide setting a block set to
 *      "Default" (auto) follows, so every section shares one source of truth
 *      instead of each block carrying its own value.
 *   3. built-in alternation — used when the global default is "alternate" (or
 *      unset): plain → tint → plain → soft by position, so no two neighbours
 *      look identical.
 */
export function surface(
  index = 0,
  override?: string,
): {kind: SurfaceKind; className: string} {
  // A concrete per-block choice beats everything.
  let kind: SurfaceKind | undefined =
    override && SURFACE_KINDS.includes(override as SurfaceKind)
      ? (override as SurfaceKind)
      : undefined;

  if (!kind) {
    // "Default"/auto/unset on the block → follow the global scheme.
    const scheme = getSectionBgScheme();
    kind = SURFACE_KINDS.includes(scheme as SurfaceKind)
      ? // A fixed global kind: every section uses it.
        (scheme as SurfaceKind)
      : // "alternate" (or anything unknown): cycle so bands never touch.
        (['plain', 'tint', 'plain', 'soft'][index % 4] as SurfaceKind);
  }

  // The actual wash + dark-mode tuning lives in common.css (`.ap-surface-*`)
  // so each surface can differ between light and dark schemes. Here we just
  // pick the class + shared chrome (isolation, divider, backdrop clipping).
  switch (kind) {
    case 'tint':
      return {kind, className: 'ap-surface-tint relative isolate border-y border-divider'};
    case 'soft':
      return {kind, className: 'ap-surface-soft relative isolate border-y border-divider bg-alt'};
    case 'edu':
      return {
        kind,
        className: 'ap-surface-edu relative isolate overflow-hidden border-y border-divider',
      };
    default:
      return {kind, className: 'relative isolate'};
  }
}

/** Education glyphs that drift behind an `edu` surface. */
const EDU_ICONS = [
  'school', 'menu_book', 'auto_stories', 'workspace_premium', 'public',
  'lightbulb', 'science', 'calculate', 'emoji_events', 'edit_note',
  'travel_explore', 'psychology',
];

/**
 * Ambient education-themed backdrop: softly drifting book / cap / globe glyphs
 * plus a faint dot grid. Purely decorative — aria-hidden, never interactive,
 * and every animation is a CSS `ap-*` utility so the Animations effect toggle
 * and `prefers-reduced-motion` both switch it off.
 */
export function EducationBackdrop({
  count = 12,
  /** Glyph/grid colour. Pass a light colour when sitting on a dark ground. */
  color = 'rgb(var(--be-primary, 80 56 237))',
  opacity = 0.09,
}: {
  count?: number;
  color?: string;
  opacity?: number;
}) {
  // Deterministic scatter (no Math.random) so SSR/rerenders stay stable and
  // the layout doesn't jump between renders.
  const items = Array.from({length: count}, (_, i) => {
    const icon = EDU_ICONS[i % EDU_ICONS.length];
    // Cheap hash-ish spread across the section.
    const left = (i * 37) % 95;
    const top = (i * 53) % 85;
    const size = 22 + ((i * 13) % 26);
    const float = i % 2 === 0 ? 'ap-float' : 'ap-float-slow';
    const delay = `ap-delay-${(i % 3) + 1}`;
    return {icon, left, top, size, float, delay, key: i};
  });

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      {/* Faint dot grid for texture. */}
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: `radial-gradient(${color} 1px, transparent 1px)`,
          backgroundSize: '26px 26px',
          // The dots are drawn at full colour, so damp the whole layer instead.
          opacity: opacity * 2,
        }}
      />
      {items.map(it => (
        <span
          key={it.key}
          className={`material-icons absolute ${it.float} ${it.delay} select-none`}
          style={{
            left: `${it.left}%`,
            top: `${it.top}%`,
            fontSize: it.size,
            color,
            opacity,
          }}
        >
          {it.icon}
        </span>
      ))}
    </div>
  );
}

/**
 * Consistent, styled section title: optional eyebrow pill, a two-tone heading
 * (text after a `|` picks up the brand gradient), an optional subtitle, and a
 * short gradient rule underneath so headings feel designed rather than default.
 */
export function SectionHeading({
  eyebrow,
  title,
  subtitle,
  align = 'center',
  className = '',
}: {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  align?: 'center' | 'start';
  className?: string;
}) {
  if (!title && !eyebrow && !subtitle) return null;
  const centered = align === 'center';
  const [main, hi] = String(title ?? '').split('|');

  return (
    <div className={`${centered ? 'text-center' : 'text-start'} ${className}`}>
      <EyebrowPill text={eyebrow} align={centered ? undefined : 'start'} />
      {title ? (
        <h2 className="text-2xl font-extrabold tracking-tight md:text-[34px] md:leading-[1.15]">
          {main}
          {hi ? (
            <>
              {' '}
              <span className="bg-gradient-to-r from-primary to-[#7c3aed] bg-clip-text text-transparent">
                {hi.trim()}
              </span>
            </>
          ) : null}
        </h2>
      ) : null}
      {/* Short gradient rule — the small flourish that reads as "designed". */}
      {title ? (
        <span
          aria-hidden
          className={`mt-12 block h-4 w-56 rounded-full bg-gradient-to-r from-primary to-[#7c3aed] ${
            centered ? 'mx-auto' : ''
          }`}
        />
      ) : null}
      {subtitle ? (
        <p className={`mt-14 text-muted ${centered ? 'mx-auto max-w-[640px]' : 'max-w-[640px]'}`}>
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

/** Convenience wrapper: a `<section>` already carrying its surface + backdrop. */
export function Section({
  index,
  bgStyle,
  bgColor,
  className = '',
  children,
}: {
  index?: number;
  bgStyle?: string;
  bgColor?: string;
  className?: string;
  children: ReactNode;
}) {
  const s = surface(index, bgStyle);
  // An explicit colour from the block settings always wins over the surface
  // wash (which then lives on the `.ap-surface-*` class, so suppress it).
  return (
    <section
      className={`${bgColor ? 'relative isolate border-y border-divider' : s.className} px-24 py-64 ${className}`}
      style={bgColor ? {backgroundColor: bgColor} : undefined}
    >
      {s.kind === 'edu' && !bgColor ? <EducationBackdrop /> : null}
      {children}
    </section>
  );
}
